// ============================================================
// token-store.ts — JWT token persistence and inspection helpers
// ============================================================

const TOKEN_KEY = 'counter_pump_auth_token';

/** Save auth token to localStorage. */
export function saveToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/** Retrieve auth token from localStorage (null if absent). */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/** Remove auth token from localStorage. */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Decode the payload section of a JWT (no signature verification —
 * the server is authoritative, this is just for display purposes).
 */
function decodePayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // Base64url → Base64 → JSON
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Returns true if the token is expired (or un-parseable).
 * Adds a 30-second grace window so we don't use a token that's about to expire.
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodePayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return nowSeconds >= payload.exp - 30;
}

/** Extract display_name from the JWT payload (null if missing). */
export function getDisplayName(token: string): string | null {
  const payload = decodePayload(token);
  if (!payload || typeof payload.display_name !== 'string') return null;
  return payload.display_name;
}
